import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Newspaper, Images, Mic, MoreHorizontal, PenLine, LogOut, X, ExternalLink } from "lucide-react";
import { LOGO_URL } from "../lib/api";

const ROLE_LABEL = { admin: "Editor", reporter: "Reporter", contributor: "Community Voice", coach: "Coach" };
const initials = (n = "") => n.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

export function MobileTopBar({ user, title, onLogout }) {
  return (
    <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-4 py-2.5 backdrop-blur lg:hidden" data-testid="admin-mobile-topbar">
      <div className="flex min-w-0 items-center gap-2.5">
        <img src={LOGO_URL} alt="News-Leader" className="h-7 w-auto shrink-0 object-contain" />
        <span className="truncate border-l border-slate-200 pl-2.5 text-sm font-bold text-[var(--navy)]">{title}</span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--navy)] text-xs font-bold text-white" title={`${user?.name} · ${ROLE_LABEL[user?.role] || "Staff"}`} data-testid="mobile-user-avatar">{initials(user?.name)}</span>
        <button onClick={onLogout} aria-label="Log out" data-testid="mobile-logout-btn" className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><LogOut size={17} /></button>
      </div>
    </header>
  );
}

const TAB_ICON = { "/admin": LayoutDashboard, "/admin/articles": Newspaper, "/admin/galleries": Images, "/admin/submit": Mic };
const TAB_LABEL = { "/admin": "Home", "/admin/articles": "Stories", "/admin/galleries": "Photos", "/admin/submit": "Submit" };

function Tab({ to, end, icon: Icon, label }) {
  return (
    <NavLink to={to} end={end} data-testid={`admin-tab-${label.toLowerCase()}`}
      className={({ isActive }) => `flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-semibold ${isActive ? "text-[var(--navy)]" : "text-slate-400"}`}>
      <Icon size={21} /> {label}
    </NavLink>
  );
}

export function MobileTabBar({ nav }) {
  const [more, setMore] = useState(false);
  const { pathname } = useLocation();
  const canWrite = nav.some((n) => n.to === "/admin/articles");
  const tabs = nav.filter((n) => TAB_ICON[n.to]);
  const left = tabs.slice(0, 2);
  const right = tabs.slice(2, canWrite ? 3 : 4);
  const rest = nav.filter((n) => !left.includes(n) && !right.includes(n));
  return (
    <>
      <nav className="fixed inset-x-0 bottom-0 z-40 flex items-end border-t border-slate-200 bg-white/95 px-2 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden" data-testid="admin-mobile-tabbar">
        {left.map((n) => <Tab key={n.to} to={n.to} end={n.end} icon={TAB_ICON[n.to]} label={TAB_LABEL[n.to]} />)}
        {canWrite && (
          <Link to="/admin/articles?new=1" data-testid="admin-tab-new-story" className="-mt-5 flex flex-1 flex-col items-center gap-1 pb-2 text-[10px] font-bold text-[var(--gold)]">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--gold)] text-white shadow-lg ring-4 ring-white"><PenLine size={22} /></span>
            New story
          </Link>
        )}
        {right.map((n) => <Tab key={n.to} to={n.to} end={n.end} icon={TAB_ICON[n.to]} label={TAB_LABEL[n.to]} />)}
        {rest.length > 0 && (
          <button onClick={() => setMore(true)} data-testid="admin-tab-more" className={`flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-semibold ${rest.some((n) => pathname.startsWith(n.to)) ? "text-[var(--navy)]" : "text-slate-400"}`}>
            <MoreHorizontal size={21} /> More
          </button>
        )}
      </nav>
      {more && <MoreSheet items={rest} onClose={() => setMore(false)} />}
    </>
  );
}

function MoreSheet({ items, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40 lg:hidden" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="admin-more-sheet" className="w-full rounded-t-3xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <div className="mb-2 flex items-center justify-between px-1">
          <p className="text-sm font-bold text-[var(--navy)]">More</p>
          <button onClick={onClose} aria-label="Close" data-testid="admin-more-close" className="text-slate-400"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={onClose} data-testid={`admin-more-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex flex-col items-center gap-1.5 rounded-2xl bg-slate-50 px-2 py-3 text-center text-[11px] font-semibold text-slate-600">
              <n.icon size={20} className="text-[var(--navy)]" /> {n.label}
            </NavLink>
          ))}
          <Link to="/" onClick={onClose} data-testid="admin-more-view-site" className="flex flex-col items-center gap-1.5 rounded-2xl bg-slate-50 px-2 py-3 text-center text-[11px] font-semibold text-slate-600">
            <ExternalLink size={20} className="text-[var(--navy)]" /> View site
          </Link>
        </div>
      </div>
    </div>
  );
}
